// src/admin/pages/AdminEditProduct.js
import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { FaSave, FaArrowLeft, FaImage, FaPlus, FaTrash, FaSearch, FaPalette } from "react-icons/fa";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import {
  getAdminProductById,
  updateAdminProduct,
  getAdminCategories,
  getAdminTags,
  createAdminTag,
} from "../../services/adminProducts.api";
import { uploadAdminFile, deleteAdminUploadedFile } from "../../services/adminUpload.api";
import { deleteAdminProductImage } from "../../services/adminProducts.api";
import { FaBox } from "react-icons/fa";
import { FiChevronRight } from "react-icons/fi";
import { FaCheck } from "react-icons/fa";
import { FiAlertCircle } from "react-icons/fi";
import { useRef } from "react";
import {
  TagIcon as TagOutline,
} from "@heroicons/react/24/outline";

import {
  TagIcon as TagSolid,
} from "@heroicons/react/24/solid";

const shakeClass = "animate-[shake_0.4s_ease-in-out]";

const shakeKeyframes = `
@keyframes shake {
  0% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  50% { transform: translateX(4px); }
  75% { transform: translateX(-4px); }
  100% { transform: translateX(0); }
}
`;

const FieldLabel = ({ label, hint, error, valid }) => {
  return (
    <div className="group relative flex items-center gap-1">
      <span className="text-[12px] font-medium text-gray-900">
        {label}
      </span>

      <div
        className={`
          w-[18px] h-[18px] rounded-full flex items-center justify-center
          text-[11px] font-bold transition cursor-pointer
          ${valid
            ? "bg-[#e3f1df] text-[#2e7d32]"
            : error
            ? "bg-[#fdecea] text-[#d72c0d]"
            : "bg-[#f1f2f3] text-[#6d7175] hover:bg-[#e1e3e5]"}
        `}
      >
        {valid ? <FaCheck className="text-[10px]" /> : "?"}
      </div>

      <div className="absolute bottom-full mb-2 left-0 hidden group-hover:block z-50 w-[240px] bg-white text-[#202223] text-[12px] border border-[#e1e3e5] rounded-lg px-3 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.08)]">
        {error ? (
          <span className="text-[#d72c0d]">{error}</span>
        ) : (
          hint
        )}
      </div>
    </div>
  );
};

const COLOR_PALETTE = [
  // NEUTRALS
  { name: "Black", hex: "#000000" },
  { name: "White", hex: "#FFFFFF" },
  { name: "Gray", hex: "#808080" },
  { name: "Light Gray", hex: "#D3D3D3" },
  { name: "Dark Gray", hex: "#4F4F4F" },
  { name: "Charcoal", hex: "#36454F" },
  { name: "Silver", hex: "#C0C0C0" },
  { name: "Smoke", hex: "#738276" },
  { name: "Ash", hex: "#B2BEB5" },
  { name: "Ivory", hex: "#FFFFF0" },

  // REDS
  { name: "Red", hex: "#FF0000" },
  { name: "Dark Red", hex: "#8B0000" },
  { name: "Crimson", hex: "#DC143C" },
  { name: "Scarlet", hex: "#FF2400" },
  { name: "Burgundy", hex: "#800020" },
  { name: "Wine", hex: "#722F37" },
  { name: "Rose Red", hex: "#C21E56" },
  { name: "Cherry", hex: "#DE3163" },
  { name: "Ruby", hex: "#E0115F" },
  { name: "Coral Red", hex: "#FF4040" },

  // PINKS
  { name: "Pink", hex: "#FFC0CB" },
  { name: "Hot Pink", hex: "#FF69B4" },
  { name: "Baby Pink", hex: "#F4C2C2" },
  { name: "Blush", hex: "#DE5D83" },
  { name: "Rose", hex: "#FF007F" },
  { name: "Fuchsia", hex: "#FF00FF" },
  { name: "Magenta", hex: "#FF0090" },
  { name: "Dusty Pink", hex: "#DCAE96" },
  { name: "Peach Pink", hex: "#FFDAB9" },
  { name: "Salmon", hex: "#FA8072" },

  // BLUES
  { name: "Blue", hex: "#0000FF" },
  { name: "Navy", hex: "#001F3F" },
  { name: "Royal Blue", hex: "#4169E1" },
  { name: "Sky Blue", hex: "#87CEEB" },
  { name: "Baby Blue", hex: "#89CFF0" },
  { name: "Steel Blue", hex: "#4682B4" },
  { name: "Denim", hex: "#1560BD" },
  { name: "Cobalt", hex: "#0047AB" },
  { name: "Ocean", hex: "#4F42B5" },
  { name: "Teal Blue", hex: "#367588" },

  // GREENS
  { name: "Green", hex: "#00A651" },
  { name: "Dark Green", hex: "#006400" },
  { name: "Olive", hex: "#808000" },
  { name: "Mint", hex: "#98FF98" },
  { name: "Emerald", hex: "#50C878" },
  { name: "Lime", hex: "#00FF00" },
  { name: "Forest", hex: "#228B22" },
  { name: "Moss", hex: "#8A9A5B" },
  { name: "Sage", hex: "#9CAF88" },
  { name: "Sea Green", hex: "#2E8B57" },

  // YELLOWS
  { name: "Yellow", hex: "#FFFF00" },
  { name: "Gold", hex: "#FFD700" },
  { name: "Mustard", hex: "#FFDB58" },
  { name: "Amber", hex: "#FFBF00" },
  { name: "Lemon", hex: "#FFF44F" },
  { name: "Flax", hex: "#EEDC82" },
  { name: "Butter", hex: "#F6E2B3" },
  { name: "Honey", hex: "#FFC30B" },
  { name: "Sand", hex: "#C2B280" },
  { name: "Sunflower", hex: "#FFC512" },

  // ORANGE
  { name: "Orange", hex: "#FFA500" },
  { name: "Burnt Orange", hex: "#CC5500" },
  { name: "Tangerine", hex: "#F28500" },
  { name: "Coral", hex: "#FF7F50" },
  { name: "Peach", hex: "#FFE5B4" },
  { name: "Apricot", hex: "#FBCEB1" },
  { name: "Rust", hex: "#B7410E" },
  { name: "Pumpkin", hex: "#FF7518" },
  { name: "Terracotta", hex: "#E2725B" },
  { name: "Carrot", hex: "#ED9121" },

  // PURPLE
  { name: "Purple", hex: "#800080" },
  { name: "Violet", hex: "#8F00FF" },
  { name: "Lavender", hex: "#E6E6FA" },
  { name: "Plum", hex: "#8E4585" },
  { name: "Orchid", hex: "#DA70D6" },
  { name: "Lilac", hex: "#C8A2C8" },
  { name: "Mauve", hex: "#E0B0FF" },
  { name: "Amethyst", hex: "#9966CC" },
  { name: "Eggplant", hex: "#614051" },
  { name: "Grape", hex: "#6F2DA8" },

  // BROWNS
  { name: "Brown", hex: "#8B4513" },
  { name: "Dark Brown", hex: "#5C4033" },
  { name: "Light Brown", hex: "#C4A484" },
  { name: "Chocolate", hex: "#7B3F00" },
  { name: "Coffee", hex: "#6F4E37" },
  { name: "Mocha", hex: "#967969" },
  { name: "Tan", hex: "#D2B48C" },
  { name: "Beige", hex: "#F5F5DC" },
  { name: "Camel", hex: "#C19A6B" },
  { name: "Khaki", hex: "#F0E68C" },
];

function AdminEditProduct() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { lang } = useContext(AdminLanguageContext);

  // refs for required fields
const refTitleEn = useRef(null);
const refMaterialEn = useRef(null);
const refMaterialAr = useRef(null);
const refAgeRange = useRef(null);
const refPrice = useRef(null);
const refPublicId = useRef(null);
const refCategories = useRef(null);
const refMedia = useRef(null);

  const [isOpen, setIsOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // EXTRA PRODUCT STATES (for edit)
  const [publicId, setPublicId] = useState("");

  const [isBestseller, setIsBestseller] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [isQuicklySoldOut, setIsQuicklySoldOut] = useState(false);
  const [onSale, setOnSale] = useState(false);
  const [salePercent, setSalePercent] = useState(0);
  
  const [materialEn, setMaterialEn] = useState("");
  const [materialAr, setMaterialAr] = useState("");

  const [ageRange, setAgeRange] = useState("");

  const [selectedFileNames, setSelectedFileNames] = useState({});

  const [leaveTarget, setLeaveTarget] = useState(null);

  // relations
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  // variants (flat list)
  const [variants, setVariants] = useState([]);

  const [discountInput, setDiscountInput] = useState("");

  // lists
  const [allCategories, setAllCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);

  // PRODUCT STATES
  const [titleEn, setTitleEn] = useState("");
  const [titleAr, setTitleAr] = useState("");
  const [price, setPrice] = useState("");
  const [imageUrl, setImageUrl] = useState("");

  // old states in your file (kept to avoid “changing everything else”)
  const [stock, setStock] = useState("");
  const [category, setCategory] = useState("");
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [realProductId, setRealProductId] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const [showValidation, setShowValidation] = useState(false);
  const [shake, setShake] = useState(false);

  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  
  const [originalData, setOriginalData] = useState(null);

  const [descEn, setDescEn] = useState("");
  const [descAr, setDescAr] = useState("");
  const [dimensionsEn, setDimensionsEn] = useState("");
const [dimensionsAr, setDimensionsAr] = useState("");
  const [isTagsOpen, setIsTagsOpen] = useState(false);

  const [customColorName, setCustomColorName] = useState("");

  const [images, setImages] = useState([]);
  const [uploadingIndex, setUploadingIndex] = useState(null);

  // GLOBAL alt for all images (GemStone-style)
const [globalAltEn, setGlobalAltEn] = useState("");
const [globalAltAr, setGlobalAltAr] = useState("");


const [showColorModal, setShowColorModal] = useState(false);
const [selectedColor, setSelectedColor] = useState(null);
const [customHex, setCustomHex] = useState("");
const [customName, setCustomName] = useState("");
const [colorSearch, setColorSearch] = useState("");
const [selectedVariantIndex, setSelectedVariantIndex] = useState(null);

// CATEGORY
const [categorySearch, setCategorySearch] = useState("");
const [isCategoryOpen, setIsCategoryOpen] = useState(false);

// TAG
const [tagSearch, setTagSearch] = useState("");
const [isTagOpen, setIsTagOpen] = useState(false);

// OPTIONAL (safe)
const [showTagModal, setShowTagModal] = useState(false);

const [newTagEn, setNewTagEn] = useState("");
const [newTagAr, setNewTagAr] = useState("");
const [tagLoading, setTagLoading] = useState(false);
const [tagError, setTagError] = useState("");
const [creatingTag, setCreatingTag] = useState(false);
const [showTagToast, setShowTagToast] = useState(false);

const isValidHex = (hex) => {
  return /^#([0-9A-F]{3}){1,2}$/i.test(hex.trim());
};

const isHexValid = isValidHex(customHex);

const isPublicIdValid = publicId.trim().length > 0;
const isNameValid = titleEn.trim().length > 0;
const isMaterialEnValid = materialEn.trim().length > 0;
const isMaterialArValid = materialAr.trim().length > 0;
const isAgeValid = true;
const isPriceValid = price !== "" && Number(price) > 0;
const isCategoryValid = selectedCategories.length > 0;
const isMediaValid = images.length > 0;

const inputClass = (hasError) => `
w-full
h-[36px]
px-3
text-[13px]
text-[#202223]
bg-white
border rounded-[8px]
outline-none
transition
focus:border-[#2c6ecb]
focus:ring-1 focus:ring-[#2c6ecb]/20
${hasError ? "border-red-500 bg-red-50" : "border-gray-500"}
${shake ? shakeClass : ""}
`;

const renderProductsIcon = (active) => {
  const Icon = active ? TagOutline : TagSolid;

  return (
    <div className="relative w-[18px] h-[18px] flex items-center justify-center">
      <Icon
        className={`
          w-[18px] h-[18px]
          ${active ? "text-[#202223]" : "text-gray-900"}
          transform scale-x-[-1]
        `}
      />
    </div>
  );
};

  const TEXT = {
    EN: {
      loading: "Loading product...",
      back: "Back to Products",
      title: publicId
  ? `Edit Product (ID: ${publicId})`
  : "Edit Product",
      error: "Please fill all required fields.",
      basic: "Basic Info",
      quicklySoldOut: "Quickly sold",
dimensionsComposition: "Dimensions and composition",
dimensionsCompositionEn: "Dimensions and composition (EN)",
dimensionsCompositionAr: "Dimensions and composition (AR)",
      desc: "Descriptions",
      pricing: "Pricing & Flags",
      discountedPrice: "Discounted Price",
      image: "Product Image",
      titleEn: "Name (EN)",
      titleAr: "Name (AR)",
      materialEn: "Material (EN)",
      materialAr: "Material (AR)",
      ageRange: "Age (years)",
      descEn: "Description (EN)",
      descAr: "Description (AR)",
      price: "Price ($)",
      publicId: "Public ID",
      flags: "Flags",
      bestseller: "Bestseller",
      new: "New",
      sale: "On Sale",
      salePercent: "Sale %",
      categories: "Collections",
      tags: "Tags",
      variants: "Variants (Colors / Sizes / Qty)",
      addColor: "Add Color",
      addSize: "Add Size",
      colorName: "Color name",
      colorHex: "Color hex",
      size: "Size",
      qty: "Qty",
      remove: "Remove",
      cancel: "Cancel",
      save: "Save",
      saving: "Saving...",
      update: "Update Product",
      selectAtLeastOneCategory: "Select at least 1 collection.",
      sortOrder: "Sort order",
    },
    AR: {
      loading: "جاري تحميل المنتج...",
      back: "العودة إلى المنتجات",
      title: publicId
  ? `تعديل المنتج (رقم: ${publicId})`
  : "تعديل المنتج",
      error: "يرجى تعبئة جميع الحقول المطلوبة.",
      basic: "المعلومات الأساسية",
      discountedPrice: "السعر بعد التخفيض",
      desc: "الوصف",
      pricing: "السعر والخصائص",
      image: "صورة المنتج",
      titleEn: "الاسم (إنجليزي)",
      titleAr: "الاسم (عربي)",
      materialEn: "الخامة (إنجليزي)",
      materialAr: "الخامة (عربي)",
      ageRange: "العمر (سنوات)",
      descEn: "الوصف (إنجليزي)",
      descAr: "الوصف (عربي)",
      price: "السعر ($)",
      publicId: "المعرّف العام",
      flags: "الخصائص",
      bestseller: "الأكثر مبيعاً",
      quicklySoldOut: "يباع بسرعة",
dimensionsComposition: "الأبعاد والتركيب",
dimensionsCompositionEn: "الأبعاد والتركيب (EN)",
dimensionsCompositionAr: "الأبعاد والتركيب (AR)",
      new: "جديد",
      sale: "تخفيض",
      salePercent: "نسبة التخفيض %",

      categories: "التصنيفات",
      tags: "الوسوم",
      variants: "المتغيرات (ألوان / مقاسات / كمية)",
      addColor: "إضافة لون",
      addSize: "إضافة مقاس",
      colorName: "اسم اللون",
      colorHex: "كود اللون",
      size: "المقاس",
      qty: "الكمية",
      remove: "حذف",

      cancel: "إلغاء",
      save: "حفظ",
      saving: "جاري الحفظ...",
      update: "تحديث المنتج",
      selectAtLeastOneCategory: "اختر تصنيف واحد على الأقل.",
      sortOrder: "ترتيب العرض",
    },
  }[lang];

  useEffect(() => {
  if (!originalData) return;

  const current = {
        publicId,
        titleEn,
        titleAr,
        materialEn,
        materialAr,
        dimensionsEn,
dimensionsAr,
isBestseller,
isNew,
isQuicklySoldOut,
        ageRange,
        price,
        descEn,
        descAr,
        onSale,                     // ✅ ADD
        salePercent: Number(salePercent || 0), // ✅ ADD
        images: JSON.stringify(images || []),
        categories: JSON.stringify(selectedCategories || []),
        tags: JSON.stringify(selectedTags || []),
        variants: JSON.stringify(variants || []),
      };

  const isChanged = Object.keys(originalData).some(
    (key) => originalData[key] !== current[key]
  );

  setIsDirty(isChanged);
}, [
  publicId,
  titleEn,
  titleAr,
  materialEn,
  materialAr,
  ageRange,
  price,
  descEn,
  descAr,
  dimensionsEn,
dimensionsAr,
isBestseller,
isNew,
isQuicklySoldOut,
onSale,          // ✅ ADD
  salePercent,      // ✅ ADD
  images,
  selectedCategories,
  selectedTags,
  variants,
  originalData,
]);

  // Load product + categories + tags
  useEffect(() => {
    let alive = true;
    setLoading(true);

    Promise.all([getAdminProductById(id), getAdminCategories(), getAdminTags()])
      .then(([p, cats, tags]) => {
        if (!alive) return;

        // product
        setTitleEn(p.name_en || "");
        setTitleAr(p.name_ar || "");
        setDescEn(p.description_en || "");
        setDescAr(p.description_ar || "");
        setDimensionsEn(p.short_desc_en || "");
setDimensionsAr(p.short_desc_ar || "");
        setMaterialEn(p.material_en || "");
        setMaterialAr(p.material_ar || "");
        setAgeRange(p.age_range ?? "");
        setPrice(p.base_price ?? "");
        setPublicId(p.public_id || "");
        setRealProductId(p.id);
        setImages(
  Array.isArray(p.images)
    ? p.images.map((img) => ({
        ...img,
        media_type:
          img.media_type ||
          (/\.(mp4|webm|ogg)$/i.test(img.url) ? "video" : "image"),
      }))
    : []
);


        setIsBestseller(!!p.is_bestseller);
        setIsNew(!!p.is_new_in);
        setIsQuicklySoldOut(!!p.is_quickly_sold_out);
        setOnSale(!!p.on_sale);
        setSalePercent(Number(p.sale_percent || 0));

        setSelectedCategories(Array.isArray(p.category_ids) ? p.category_ids : []);
        setSelectedTags(Array.isArray(p.tag_ids) ? p.tag_ids : []);
        setVariants(Array.isArray(p.variants) ? p.variants : []);
        
        // lists
        setAllCategories(Array.isArray(cats) ? cats : []);
        setAllTags(Array.isArray(tags) ? tags : []);

        // optional image if your backend returns it (safe)
        setImageUrl(p.image || "");
        setOriginalData({
              publicId: p.public_id || "",
              titleEn: p.name_en || "",
              titleAr: p.name_ar || "",
              materialEn: p.material_en || "",
              materialAr: p.material_ar || "",
              ageRange: p.age_range ?? "",
              price: p.base_price ?? "",
              descEn: p.description_en || "",
              descAr: p.description_ar || "",
isBestseller: !!p.is_bestseller,
isNew: !!p.is_new_in,
isQuicklySoldOut: !!p.is_quickly_sold_out,
onSale: !!p.on_sale,
dimensionsEn: p.short_desc_en || "",
dimensionsAr: p.short_desc_ar || "",
              salePercent: Number(p.sale_percent || 0), // ✅ ADD
              images: JSON.stringify(p.images || []),
              categories: JSON.stringify(p.category_ids || []),
              tags: JSON.stringify(p.tag_ids || []),
              variants: JSON.stringify(p.variants || []),
            });
      })
      .catch((e) => {
        console.error(e);
        setError(e.message || "Error loading product.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [id]);

  // Sale UX: if toggle off => percent becomes 0
  useEffect(() => {
    if (!onSale) setSalePercent(0);
  }, [onSale]);

  // -------- helpers: categories/tags ----------
  const toggleIdInArray = (arr, idNum) => {
    const has = arr.includes(idNum);
    if (has) return arr.filter((x) => x !== idNum);
    return [...arr, idNum];
  };

  // -------- variants editor helpers ----------
  const groupedVariants = useMemo(() => {
    const map = new Map();
  
    (variants || []).forEach((v) => {
      const colorName = v.color_name || "Unknown";
      const colorHex = v.color_hex || "#cccccc";
      const key = `${colorName}|||${colorHex}`;
  
      if (!map.has(key)) {
        map.set(key, {
          color_name: colorName,
          color_hex: colorHex,
          rows: [],
        });
      }
  
      map.get(key).rows.push({
        size: v.size != null ? String(v.size) : "",
        stock_qty: Number(v.stock_qty || 0),
      });
    });
  
    return Array.from(map.values());
  }, [variants]);

 const handleBackClick = () => {
  if (isDirty) {
    setLeaveTarget("/admin/products");
    setShowLeaveConfirm(true);
  } else {
    navigate("/admin/products");
  }
};

  const filteredColors = useMemo(() => {
    const search = colorSearch.trim().toLowerCase();
  
    if (!search) return COLOR_PALETTE;
  
    return COLOR_PALETTE.filter((c) =>
      c.name.toLowerCase().includes(search)
    );
  }, [colorSearch]);

  const rebuildFlatVariantsFromGroups = (groups) => {
  const flat = [];

  groups.forEach((g) => {
    if (!g.rows.length) {
      flat.push({
        color_name: g.color_name,
        color_hex: g.color_hex,
        size: "",
        stock_qty: 0,
      });
      return;
    }

    g.rows.forEach((r) => {
      flat.push({
        color_name: g.color_name,
        color_hex: g.color_hex,
   size: String(r.size ?? ""),
        stock_qty: Number(r.stock_qty || 0),
      });
    });
  });

  setVariants(flat);
};


 const addColorGroup = (color) => {
  if (!color) return;

  setVariants((prev) => [
    ...(prev || []),
    {
      color_name: color.name,
      color_hex: color.hex,
      size: "",
      stock_qty: 0,
    },
  ]);
};

const scrollToFirstError = () => {
  const fields = [
    { condition: !titleEn.trim(), ref: refTitleEn },
    { condition: !materialEn.trim(), ref: refMaterialEn },
    { condition: !materialAr.trim(), ref: refMaterialAr },
    { condition: !publicId.trim(), ref: refPublicId },
    { condition: price === "" || Number(price) <= 0, ref: refPrice },
    { condition: !selectedCategories.length, ref: refCategories },
    { condition: images.length === 0, ref: refMedia },
  ];

  const first = fields.find((f) => f.condition && f.ref.current);

  if (first) {
    first.ref.current.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    setTimeout(() => {
      first.ref.current?.focus?.();
    }, 300);
  }
};

const handleCreateTag = async () => {
  if (!newTagEn.trim()) {
    setTagError(lang === "AR" ? "الاسم الإنجليزي مطلوب" : "English name required");
    return;
  }

  try {
    setCreatingTag(true); // ✅ FIX
    setTagError("");

    const created = await createAdminTag({
      label_en: newTagEn.trim(),
      label_ar: newTagAr.trim(),
    });

    // add to list
    setAllTags((prev) => [created, ...prev]);

    // auto select
    setSelectedTags((prev) => [...prev, Number(created.id)]);

    // reset
    setNewTagEn("");
    setNewTagAr("");

    // ✅ THIS IS WHAT YOU ASKED ABOUT
    setShowTagToast(true);
    setTimeout(() => setShowTagToast(false), 2000);

    setShowTagModal(false);
    setIsTagOpen(false);

  } catch (err) {
    setTagError(
  err.message || (lang === "AR" ? "فشل الإنشاء" : "Create failed")
);
  } finally {
    setCreatingTag(false); // ✅ FIX
  }
};

const addImageRow = () => {
  setImages((prev) => [
    ...(prev || []),
    {
      id: null,
      url: "",
      alt_en: "",
      alt_ar: "",
      color_name: "",
      sort_order: (prev?.length || 0) + 1,
      media_type: "image", // ✅ ADD
    },
  ]);
};


const updateImageRow = (idx, patch) => {
  setImages((prev) =>
    prev.map((img, i) => (i === idx ? { ...img, ...patch } : img))
  );
};

const removeImageRow = async (idx) => {
  const img = images?.[idx];

  // ✅ If image exists in DB → delete DB record (your existing logic)
  if (img?.id) {
    try {
      await deleteAdminProductImage(img.id);
    } catch (e) {
      console.error("Delete DB image failed:", e);
    }
  }

  // ✅ If it's an uploaded file path → delete from disk (GemStone behavior)
  if (
    img?.url &&
    (img.url.startsWith("/images/") || img.url.startsWith("/videos/"))
  ) {
    try {
      await deleteAdminUploadedFile(img.url);
    } catch (e) {
      console.error("Delete uploaded file failed:", e);
    }
  }

  // ✅ Remove from UI
  setImages((prev) => (prev || []).filter((_, i) => i !== idx));
};


  const removeColorGroup = (idx) => {
  const groups = groupedVariants.filter((_, i) => i !== idx);
  rebuildFlatVariantsFromGroups(groups);
};

  const updateColorGroup = (idx, patch) => {
  const groups = groupedVariants.map((g, i) =>
    i === idx ? { ...g, ...patch } : g
  );
  rebuildFlatVariantsFromGroups(groups);
};

  const addSizeRow = (idx) => {
  const groups = groupedVariants.map((g, i) =>
    i === idx
      ? { ...g, rows: [...g.rows, { size: "", stock_qty: 0 }] }
      : g
  );
  rebuildFlatVariantsFromGroups(groups);
};

  const removeSizeRow = (idx, rowIdx) => {
  const groups = groupedVariants.map((g, i) =>
    i === idx
      ? { ...g, rows: g.rows.filter((_, j) => j !== rowIdx) }
      : g
  );
  rebuildFlatVariantsFromGroups(groups);
};

  const updateSizeRow = (idx, rowIdx, patch) => {
  const groups = groupedVariants.map((g, i) => {
    if (i !== idx) return g;
    const newRows = g.rows.map((r, j) =>
      j === rowIdx ? { ...r, ...patch } : r
    );
    return { ...g, rows: newRows };
  });
  rebuildFlatVariantsFromGroups(groups);
};

const filteredCategories = useMemo(() => {
  const search = categorySearch.toLowerCase().trim();
  if (!search) return allCategories;

  return allCategories.filter((c) =>
    (c.name_en || c.name_ar || "")
      .toLowerCase()
      .includes(search)
  );
}, [categorySearch, allCategories]);

const filteredTags = useMemo(() => {
  const search = tagSearch.toLowerCase().trim();
  if (!search) return allTags;

  return allTags.filter((t) =>
    (t.label_en || t.slug || "")
      .toLowerCase()
      .includes(search)
  );
}, [tagSearch, allTags]);

const handleConfirmUpdate = () => {
  confirmSave({ preventDefault: () => {} });
};

  // -------- save ----------
  const confirmSave = async (e) => {
  e?.preventDefault?.();
    setError("");

    if (!titleEn.trim() || !publicId.trim() || price === "" || Number(price) < 0) {
      setError(TEXT.error);
      return;
    }

    if (images.length === 0) {
  setError(lang === "AR" ? "يجب إضافة صورة واحدة على الأقل" : "At least 1 image required");
  scrollToFirstError();
  return;
}

if (Number(price) <= 0) {
  setError(lang === "AR" ? "السعر يجب أن يكون أكبر من 0" : "Price must be greater than 0");
  scrollToFirstError();
  return;
}

    if (!selectedCategories.length) {
      setError(TEXT.selectAtLeastOneCategory);
      return;
    }

   if (
  !materialEn.trim() ||
  !materialAr.trim()
) {
  setError(TEXT.error);
  return;
}


    // Clean variants: remove empty sizes + normalize
    const cleanedVariants = (variants || [])
  .map((v) => {
    const color = (v.color_name || "Unknown").trim();
    const size = String(v.size ?? "");

    return {
      sku: `${publicId}-${color}-${size}`.replace(/\s+/g, "-").toUpperCase(),
      color_name: color,
      color_hex: (v.color_hex || "#cccccc").trim(),
      size,
      stock_qty: Number(v.stock_qty || 0),
    };
  })
  .filter((v) => v.size.trim());

const cleanedImages = (images || [])
  .filter((i) => i.url)
  .map((i, idx) => ({
    url: String(i.url || "").trim(),
    alt_en: String(i.alt_en || "").trim(),
    alt_ar: String(i.alt_ar || "").trim(),
    color_name: String(i.color_name || "").trim(),
    sort_order: Number(i.sort_order ?? idx + 1),
    media_type: i.media_type || "image", // ✅ safe (GemStone-style)
  }));


    setSaving(true);
    try {
      await updateAdminProduct(id, {
        name_en: titleEn.trim(),
        name_ar: titleAr.trim(),
        description_en: descEn.trim(),
description_ar: descAr.trim(),
short_desc_en: dimensionsEn.trim(),
short_desc_ar: dimensionsAr.trim(),
material_en: materialEn.trim(),
        material_ar: materialAr.trim(),
        age_range: ageRange.trim(),

        public_id: publicId.trim(),
        base_price: Number(price).toFixed(2),

        is_bestseller: isBestseller,
is_new_in: isNew,
is_quickly_sold_out: isQuicklySoldOut,
on_sale: onSale,
        sale_percent: onSale ? Number(salePercent || 0) : 0,

        categories: selectedCategories,
        tags: selectedTags,
        variants: cleanedVariants,
        images: cleanedImages,
      });

      setIsDirty(false);
      setOriginalData({
  publicId,
  titleEn,
  titleAr,
  materialEn,
  materialAr,
  dimensionsEn,
  dimensionsAr,
  isBestseller,
  isNew,
  isQuicklySoldOut,
  ageRange,
  price,
  descEn,
  descAr,
  onSale,
  salePercent: Number(salePercent || 0),
  images: JSON.stringify(images || []),
  categories: JSON.stringify(selectedCategories || []),
  tags: JSON.stringify(selectedTags || []),
  variants: JSON.stringify(variants || []),
});

      setShowToast(true);
      setTimeout(() => {
                 navigate("/admin/products");
                }, 1200);
    } catch (err) {
      console.error(err);
      setError(
  err.message || (lang === "AR" ? "خطأ أثناء الحفظ" : "Save error")
);
    } finally {
      setSaving(false);
    }
  };


  const handleMultiImageUpload = async (files) => {
  if (!files || !files.length) return;

  const startOrder = images.length;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    try {
      const uploaded = await uploadAdminFile(file);

      setImages((prev) => [
        ...prev,
        {
          id: null, // new upload (not from DB yet)
          url: uploaded.url,
          alt_en: globalAltEn,
          alt_ar: globalAltAr,
          color_name: "",
          sort_order: startOrder + i + 1,
          media_type: file.type?.startsWith("video/") ? "video" : "image",
        },
      ]);
    } catch (err) {
      alert(
  err.message || (lang === "AR" ? "فشل الرفع" : "Upload failed")
);
    }
  }
};

useEffect(() => {
  const handleClickOutside = (e) => {
    if (!e.target.closest(".category-dropdown")) {
      setIsCategoryOpen(false);
    }
    if (!e.target.closest(".tag-dropdown")) {
      setIsTagOpen(false);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, []);

useEffect(() => {
  setImages((prev) =>
    (prev || []).map((img) => ({
      ...img,
      alt_en: globalAltEn,
      alt_ar: globalAltAr,
    }))
  );
}, [globalAltEn, globalAltAr]);

  const discountedPrice = useMemo(() => {
  if (!onSale || !price) return "";

  const base = Number(price);

  if (discountInput !== "") {
    return Number(discountInput).toFixed(2);
  }

  if (!salePercent) return "";

  const d = base - (base * Number(salePercent)) / 100;
  return d.toFixed(2);
}, [price, salePercent, onSale, discountInput]);

const toggleTranslate = (active) => {
  if (!active) return "translate-x-0";

  return lang === "AR"
    ? "-translate-x-4" // 🔥 move left in RTL
    : "translate-x-4"; // normal LTR
};

const truncate = (text, max = 15) => {
  if (!text) return "";
  return text.length > max ? text.slice(0, max) + "..." : text;
};

const isFormValid = useMemo(() => {
  if (!titleEn.trim()) return false;
  if (!publicId.trim()) return false;
  if (price === "" || Number(price) <= 0) return false;
if (images.length === 0) return false;
  if (!materialEn.trim()) return false;
  if (!materialAr.trim()) return false;
  if (!selectedCategories.length) return false;

  return true;
}, [
  titleEn,
  publicId,
  price,
  materialEn,
  materialAr,
  ageRange,
  selectedCategories,
]);

const handleSave = () => {
  setShowValidation(true);
  setError(""); // reset first

  if (!isFormValid) {
    setError(TEXT.error); // 🔥 ADD THIS

    setShake(true);
    setTimeout(() => setShake(false), 400);

    scrollToFirstError();
    return;
  }

  setShowConfirm(true);
};

  if (loading) {
  return (
    <div className="flex bg-[#f1f1f1] min-h-screen">
      <AdminSidebar isOpen={false} setIsOpen={() => {}} />

      <div className={`flex-1 ${lang === "AR" ? "lg:mr-26" : "lg:ml-64"}`}>
        <AdminHeader setIsOpen={() => {}} />

        <main className="px-6 py-6">
          <div className="max-w-[1040px] mx-auto space-y-4 animate-pulse">

            {/* TOP BAR */}
            <div className="h-6 w-48 bg-gray-300 rounded" />

            {/* LEFT + RIGHT GRID */}
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4">

              {/* LEFT */}
              <div className="space-y-4">
                <div className="h-[300px] bg-gray-300 rounded-[12px]" />
                <div className="h-[220px] bg-gray-300 rounded-[12px]" />
                <div className="h-[200px] bg-gray-300 rounded-[12px]" />
              </div>

              {/* RIGHT */}
              <div className="space-y-4">
                <div className="h-[120px] bg-gray-300 rounded-[12px]" />
                <div className="h-[180px] bg-gray-300 rounded-[12px]" />
                <div className="h-[140px] bg-gray-300 rounded-[12px]" />
              </div>

            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

if (!loading && !realProductId) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-[#f1f1f1]">
      <div className="text-center">

        <div className="text-[18px] font-semibold text-[#202223] mb-2">
          {lang === "AR" ? "المنتج غير موجود" : "Product not found"}
        </div>

        <p className="text-sm text-gray-500 mb-4">
          {lang === "AR"
            ? "قد يكون تم حذف المنتج أو الرابط غير صحيح"
            : "This product may have been deleted or does not exist"}
        </p>

        <button
          onClick={() => navigate("/admin/products")}
          className="px-4 py-2 bg-black text-white rounded-md text-sm"
        >
          {lang === "AR" ? "العودة" : "Go back"}
        </button>

      </div>
    </div>
  );
}

  return (
    <>
     <style>{shakeKeyframes}</style>
    <div className={`flex bg-[#f1f1f1] min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className={`flex-1 ${lang === "AR" ? "lg:mr-26" : "lg:ml-64"}`}>
        <AdminHeader
  setIsOpen={setIsOpen}
  customCenter={
    <div
      className={`
        hidden sm:block
        w-full
        sm:max-w-[500px]
        max-w-full
        transition-all duration-300
        ${isDirty ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"}
      `}
    >
      <div className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-md px-3 py-2 sm:py-[4px] space-y-2 sm:space-y-0 sm:flex sm:items-center sm:justify-between">

        {/* LEFT */}
        <div className="flex items-center gap-2 text-[13px] text-gray-200 justify-between sm:justify-start">
          <FiAlertCircle className="text-white-400 text-[16px]" />
          <span>
            {lang === "AR" ? "تغييرات غير محفوظة" : "Unsaved changes"}
          </span>
        </div>

        {/* RIGHT */}
        <div className="flex gap-2 sm:gap-1.5 w-full sm:w-auto">

          {/* DISCARD */}
          <button
            type="button"
            onClick={handleBackClick}
            className="
              px-2 py-[2px]
              text-[12px]
              rounded-md
              border border-[#3a3a3a]
              bg-[#1f1f1f]
              hover:bg-[#363636]
              text-gray-200
              transition
            "
          >
            {lang === "AR" ? "إلغاء" : "Discard"}
          </button>

          {/* SAVE */}
          <button
            type="button"
            onClick={handleSave}
            disabled={!isFormValid}
            className={`
              px-2 py-[2px]
              text-[12px]
              rounded-md
              font-medium
              transition
              ${
                isFormValid
                  ? "bg-white text-black hover:bg-gray-200"
                  : "bg-gray-400 text-gray-200 cursor-not-allowed"
              }
            `}
          >
            {lang === "AR" ? "حفظ" : "Save"}
          </button>

        </div>
      </div>
    </div>
  }
/>

        <main className="px-3 sm:px-6 py-4 pb-20 sm:pb-4 mt-3">
  <div className="max-w-[1040px] mx-auto">
         {showConfirm && (
  <div className="fixed inset-0 z-[80] bg-[#202223]/40 backdrop-blur-[2px] flex items-center justify-center px-5 py-4">
    <div className="w-full max-w-[420px] overflow-hidden rounded-[18px] border border-[#e1e3e5] bg-white shadow-[0_20px_50px_rgba(0,0,0,0.18)]">
      
      <div className="border-b border-[#e1e3e5] px-5 py-4">
        <h3 className="text-[16px] font-semibold text-[#202223]">
          {lang === "AR" ? "تحديث المنتج" : "Update product"}
        </h3>
      </div>

      <div className="px-5 py-4">
        <p className="text-sm text-[#6d7175]">
          {lang === "AR"
            ? "هل أنت متأكد أنك تريد تحديث هذا المنتج؟"
            : "Are you sure you want to update this product?"}
        </p>
      </div>

      <div className="flex justify-end gap-4 border-t border-[#e1e3e5] bg-[#f6f6f7] px-5 py-4">
        
        <button
          onClick={() => setShowConfirm(false)}
          className="h-[36px] px-4 border rounded-[10px]"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          onClick={() => {
            setShowConfirm(false);
            handleConfirmUpdate(); // 🔥 IMPORTANT (not create)
          }}
          className="h-[36px] px-4 bg-[#202223] text-white rounded-[10px]"
        >
          {lang === "AR" ? "تحديث" : "Update"}
        </button>

      </div>
    </div>
  </div>
)}

{showLeaveConfirm && (
  <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center">
    <div className="w-full max-w-[420px] bg-white rounded-[18px] p-6">

      <h3 className="text-[18px] font-semibold mb-2">
        {lang === "EN" ? "Leave without saving?" : "مغادرة بدون حفظ؟"}
      </h3>

      <p className="text-sm text-gray-500 mb-6">
        {lang === "EN"
          ? "You have unsaved changes. Are you sure you want to leave?"
          : "لديك تغييرات غير محفوظة. هل أنت متأكد؟"}
      </p>

      <div className="flex justify-end gap-3">

        <button
          onClick={() => setShowLeaveConfirm(false)}
          className="px-4 py-2 border rounded"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          onClick={() => navigate(leaveTarget || "/admin/products")}
          className="px-4 py-2 bg-black text-white rounded"
        >
          {lang === "AR" ? "مغادرة" : "Leave"}
        </button>

      </div>

    </div>
  </div>
)}

{showTagModal && (
  <div className="fixed inset-0 z-[80] bg-[#202223]/40 backdrop-blur-[2px] flex items-center justify-center px-5 py-4">
    <div className="w-full max-w-[520px] overflow-hidden rounded-[18px] border border-[#e1e3e5] bg-white shadow-[0_20px_50px_rgba(0,0,0,0.18)]">
      
      {/* HEADER */}
      <div className="border-b border-[#e1e3e5] px-5 py-4">
        <h3 className="text-[16px] font-semibold text-[#202223]">
          {lang === "AR" ? "إضافة وسم جديد" : "Add new tag"}
        </h3>
      </div>

      {/* BODY */}
      <div className="px-5 py-5 space-y-4">

        <div>
          <label className="block text-[12px] font-medium text-[#6d7175] mb-1.5">
            {TEXT.tagLabelEn}
          </label>
          <input
            value={newTagEn}
            onChange={(e) => setNewTagEn(e.target.value)}
            className={inputClass(false)}
          />
        </div>

        <div>
          <label className="block text-[12px] font-medium text-[#6d7175] mb-1.5">
            {TEXT.tagLabelAr}
          </label>
          <input
            value={newTagAr}
            onChange={(e) => setNewTagAr(e.target.value)}
            className={inputClass(false)}
          />
        </div>

      </div>

      {/* FOOTER */}
      <div className="flex items-center justify-end gap-4 border-t border-[#e1e3e5] bg-[#f6f6f7] px-5 py-4">

        <button
          type="button"
          onClick={() => {
            setShowTagModal(false);
            setNewTagEn("");
            setNewTagAr("");
          }}
          className="h-[36px] px-4 rounded-[10px] border border-[#c9cccf] bg-white text-[#202223] text-sm font-medium hover:bg-[#f6f6f7]"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          type="button"
          onClick={handleCreateTag}
          disabled={creatingTag}
          className="h-[36px] px-4 rounded-[10px] bg-[#202223] text-white text-sm font-medium hover:bg-[#111213] disabled:opacity-60"
        >
          {creatingTag
            ? (lang === "AR" ? "جاري الإنشاء..." : "Creating...")
            : (lang === "AR" ? "إنشاء الوسم" : "Create tag")}
        </button>

      </div>
    </div>
  </div>
)}

{showColorModal && (
  <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl w-full max-w-[600px] p-5 shadow-xl">

      <h3 className="text-lg font-semibold mb-3">
  {lang === "AR" ? "اختر لون" : "Select Color"}
</h3>

{/* SEARCH BAR */}
<div className="mb-3">
  <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white">
    <FaSearch className="text-gray-400 text-[13px]" />

    <input
      value={colorSearch}
      onChange={(e) => setColorSearch(e.target.value)}
      placeholder={lang === "AR" ? "ابحث عن لون..." : "Search colors..."}
      className="flex-1 text-sm outline-none bg-transparent"
    />
  </div>
</div>

      {/* COLOR GRID */}
      <div className="grid grid-cols-5 gap-3 max-h-[300px] overflow-y-auto">
        {filteredColors.length === 0 && (
              <div className="col-span-5 text-center text-xs text-gray-500 py-4">
                {lang === "AR" ? "لا يوجد ألوان" : "No colors found"}
              </div>
            )}
        {filteredColors.map((c) => (
          <button
            key={c.hex}
            onClick={() => setSelectedColor(c)}
            className={`p-2 rounded-lg border text-xs text-center
              ${selectedColor?.hex === c.hex ? "border-black" : "border-gray-200"}
            `}
          >
            <div
              className="w-6 h-6 rounded-full mx-auto mb-1 border"
              style={{ backgroundColor: c.hex }}
            />
            <div>{c.name}</div>
            <div className="text-[10px] text-gray-500">{c.hex}</div>
          </button>
        ))}
      </div>

      {/* CUSTOM COLOR */}
      <div className="mt-4 border-t pt-4">
  <div className="text-sm font-medium mb-3">
    {lang === "AR" ? "لون مخصص" : "Custom Color"}
  </div>

  {/* ROW 1: PICKER + HEX */}
  <div className="flex items-center gap-3 mb-3">

    {/* COLOR PICKER */}
    <div className="relative">
      <input
        type="color"
        value={customHex || "#000000"}
        onChange={(e) => {
          setCustomHex(e.target.value.toUpperCase());
        }}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />

      <div
        className="w-10 h-10 rounded-lg border border-gray-300 shadow-sm flex items-center justify-center"
        style={{ backgroundColor: customHex || "#ffffff" }}
      >
        {!customHex && (
          <FaPalette className="text-gray-400 text-sm" />
        )}
      </div>
    </div>

    {/* HEX INPUT */}
    <input
      placeholder="#000000"
      value={customHex}
      onChange={(e) =>
        setCustomHex(e.target.value.toUpperCase())
      }
      className={`
        h-[36px] text-sm border rounded px-3 w-[120px]
        ${customHex && !isHexValid ? "border-red-400" : "border-gray-300"}
      `}
    />

  </div>

  {/* ROW 2: NAME */}
  <input
    placeholder={lang === "AR" ? "اسم اللون" : "Color name"}
    value={customName}
    onChange={(e) => setCustomName(e.target.value)}
    className="w-full h-[36px] text-sm border border-gray-300 rounded px-3 mb-3"
  />

  {/* ROW 3: ACTION */}
  <div className="flex justify-end">
    <button
      onClick={() => {
        if (!isHexValid) return;

        setSelectedColor({
          name: customName.trim() || customHex,
          hex: customHex.toUpperCase(),
        });
      }}
      disabled={!isHexValid}
      className={`
        px-4 py-2 rounded-lg text-sm font-medium
        ${isHexValid
          ? "bg-[#202223] text-white hover:bg-black"
          : "bg-gray-200 text-gray-400 cursor-not-allowed"}
      `}
    >
      {lang === "AR" ? "تحديد" : "Set"}
    </button>
  </div>

  {/* ERROR */}
  {customHex && !isHexValid && (
    <p className="text-[11px] text-red-500 mt-2">
      {lang === "AR"
        ? "أدخل كود HEX صالح مثل #000000"
        : "Enter a valid HEX code like #000000"}
    </p>
  )}
</div>

      {/* ACTIONS */}
      <div className="flex justify-end gap-3 mt-5">
        <button
          onClick={() => setShowColorModal(false)}
          className="px-4 py-2 border rounded-lg"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          onClick={() => {
            if (!selectedColor) return;

            if (selectedVariantIndex !== null) {
              updateColorGroup(selectedVariantIndex, {
                color_name: selectedColor.name,
                color_hex: selectedColor.hex,
              });

              setSelectedVariantIndex(null);
            } else {
              addColorGroup(selectedColor);
            }

            setShowColorModal(false);
            setSelectedColor(null);
            setCustomHex("");
            setCustomName("");
          }}
          className="px-4 py-2 bg-black text-white rounded-lg"
        >
          {lang === "AR" ? "تم" : "Done"}
        </button>
      </div>

    </div>
  </div>
)}

{showTagToast && (
  <div className="fixed top-24 right-6 z-[70] animate-fade-in">
    <div className="min-w-[320px] max-w-[420px] rounded-[14px] border border-[#cde7d8] bg-white shadow-[0_12px_30px_rgba(0,0,0,0.12)] overflow-hidden">
      
      <div className="flex items-start gap-3 px-4 py-3">

        <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-[#e3f1df] text-[#29845a] text-sm font-bold">
          ✓
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-[#202223]">
            {lang === "AR" ? "تم إنشاء الوسم" : "Tag created"}
          </div>

          <div className="mt-0.5 text-sm text-[#6d7175]">
            {lang === "AR"
              ? "تم إنشاء الوسم وإضافته بنجاح."
              : "The tag was created and added successfully."}
          </div>
        </div>

      </div>
    </div>
  </div>
)}

        {showToast && (
  <div className="fixed top-6 right-6 z-[70] animate-fade-in">
    <div className="min-w-[320px] max-w-[420px] rounded-[14px] border border-[#cde7d8] bg-white shadow-[0_12px_30px_rgba(0,0,0,0.12)] overflow-hidden">

      <div className="flex items-start gap-3 px-4 py-3">

        {/* ICON */}
        <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-[#e3f1df] text-[#2e7d32]">
          <FaCheck className="text-[13px]" />
        </div>

        {/* TEXT */}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-[#202223]">
            {lang === "AR" ? "تم تحديث المنتج" : "Product updated"}
          </div>

          <div className="mt-0.5 text-sm text-[#6d7175]">
            {lang === "AR"
              ? "تم حفظ التغييرات بنجاح."
              : "Your changes have been saved successfully."}
          </div>
        </div>

      </div>
    </div>
  </div>
)}

          {/* Top bar */}
          <div className="flex items-center gap-2 mb-6 text-sm text-gray-600">
  {/* Icon (back to products) */}
 <button
              onClick={handleBackClick}
              className="relative flex items-center justify-center w-8 h-8 rounded-md text-[#202223] hover:bg-gray-200 transition"
            >
              <div className="flex items-center justify-center w-6 h-6">
  {renderProductsIcon(true)}
</div>
            </button>

  {/* Arrow */}
  <FiChevronRight className="text-gray-400 text-[14px] shrink-0" />

  {/* Title */}
  <h1 className="text-gray-800 font-semibold">
    {lang === "AR"
      ? `تعديل المنتج (${publicId || ""})`
      : `Edit Product (${publicId || ""})`}
  </h1>
</div>

         {error && (
  <div className="mb-4 flex items-start gap-2 rounded-[12px] border border-[#e4c9c9] bg-[#fff5f5] px-4 py-3 text-sm text-[#8e1f1f] shadow-sm">
    <FiAlertCircle className="mt-[2px]" />
    <span>{error}</span>
  </div>
)}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="space-y-4"
          >
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-4">

        {/* LEFT COLUMN */}
<div className="contents xl:block">

  {/* CARD 1 */}
  <section className="bg-white border border-[#e3e3e3] rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
    <div className="p-4 space-y-4">
            {/* BASIC INFO */}
            <section>
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

    {/* NAME EN */}
    <div>
      <div className="mb-1">
        <FieldLabel
            label={TEXT.titleEn}
            hint={lang === "AR" ? "أدخل اسم المنتج بالإنجليزية" : "Enter product name in English"}
            valid={titleEn.trim().length > 0}
            error={
  showValidation && !titleEn.trim()
    ? (lang === "AR" ? "مطلوب" : "Required")
    : ""
}
          />
      </div>

      <input
          ref={refTitleEn}
          value={titleEn}
          onChange={(e) => setTitleEn(e.target.value)}
          className={inputClass(showValidation && !titleEn.trim())}
        />

        {showValidation && !titleEn.trim() && (
          <p className="text-[11px] text-red-500 mt-1">
            {lang === "AR" ? "هذا الحقل مطلوب" : "This field is required"}
          </p>
        )}
    </div>

    {/* NAME AR */}
    <div>
      <div className="mb-1">
        <FieldLabel
          label={TEXT.titleAr}
          hint={lang === "AR" ? "اسم عربي (اختياري)" : "Optional Arabic name"}
        />
      </div>

      <input
        value={titleAr}
        onChange={(e) => setTitleAr(e.target.value)}
        dir="rtl"
        className={inputClass(false)}
      />
    </div>

    {/* MATERIAL EN */}
    <div>
      <div className="mb-1">
        <FieldLabel
          label={TEXT.materialEn}
          hint={lang === "AR" ? "الخامة بالإنجليزية" : "Material in English"}
          valid={materialEn.trim().length > 0}
          error={showValidation && !materialEn.trim() ? (lang === "AR" ? "مطلوب" : "Required")
                        : ""
                    }
        />
      </div>

      <input
        ref={refMaterialEn}
        value={materialEn}
        onChange={(e) => setMaterialEn(e.target.value)}
        className={inputClass(showValidation && !materialEn.trim())}

      />
    </div>

    {/* MATERIAL AR */}
    <div>
      <div className="mb-1">
        <FieldLabel
          label={TEXT.materialAr}
          hint={lang === "AR" ? "الخامة بالعربية" : "Material in Arabic"}
          valid={materialAr.trim().length > 0}
          error={showValidation && !materialAr.trim() ? (lang === "AR" ? "مطلوب" : "Required")
    : ""
}
        />
      </div>

      <input
        ref={refMaterialAr}
        value={materialAr}
        onChange={(e) => setMaterialAr(e.target.value)}
        className={inputClass(showValidation && !materialAr.trim())}

      />
    </div>

  </div>
</section>

{/* DESCRIPTIONS */}
<section>
  <div className="mb-1">
    <FieldLabel
      label={TEXT.desc}
      hint={lang === "AR" ? "وصف تفصيلي للمنتج" : "Detailed product description"}
      valid={descEn.trim() || descAr.trim()}
    />
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <textarea
      rows="4"
      value={descEn}
      onChange={(e) => setDescEn(e.target.value)}
      placeholder={TEXT.descEn}
      className={`
        w-full px-3 py-2 text-sm text-[#202223]
        bg-white border border-gray-500 rounded-[8px]
        outline-none resize-none
        focus:border-[#2c6ecb]
        focus:ring-1 focus:ring-[#2c6ecb]/20
      `}
    />

    <textarea
      rows="4"
      value={descAr}
      onChange={(e) => setDescAr(e.target.value)}
      placeholder={TEXT.descAr}
      dir="rtl"
      className={`
        w-full px-3 py-2 text-sm text-[#202223]
        bg-white border border-gray-500 rounded-[8px]
        outline-none resize-none
        focus:border-[#2c6ecb]
        focus:ring-1 focus:ring-[#2c6ecb]/20
      `}
    />
  </div>
</section>

{/* DIMENSIONS AND COMPOSITION */}
<section>
  <div className="mb-1">
    <FieldLabel
      label={TEXT.dimensionsComposition}
      hint={
        lang === "AR"
          ? "أضف تفاصيل الأبعاد والتركيب التي ستظهر في صفحة المنتج"
          : "Add dimensions and composition details shown on the product page"
      }
      valid={dimensionsEn.trim().length > 0 || dimensionsAr.trim().length > 0}
    />
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <textarea
      rows="3"
      value={dimensionsEn}
      onChange={(e) => setDimensionsEn(e.target.value)}
      placeholder={TEXT.dimensionsCompositionEn}
      className="
        w-full px-3 py-2 text-sm text-[#202223]
        bg-white border border-gray-500 rounded-[8px]
        outline-none resize-none
        focus:border-[#2c6ecb]
        focus:ring-1 focus:ring-[#2c6ecb]/20
      "
    />

    <textarea
      rows="3"
      value={dimensionsAr}
      onChange={(e) => setDimensionsAr(e.target.value)}
      placeholder={TEXT.dimensionsCompositionAr}
      dir="rtl"
      className="
        w-full px-3 py-2 text-sm text-[#202223]
        bg-white border border-gray-500 rounded-[8px]
        outline-none resize-none
        focus:border-[#2c6ecb]
        focus:ring-1 focus:ring-[#2c6ecb]/20
      "
    />
  </div>
</section>

        {/* MEDIA SECTION (Shopify style — SAME AS ADD PRODUCT) */}
<section
  ref={refMedia}
  className={`${
    showValidation && images.length === 0
      ? "border border-red-500 rounded-[12px] p-2"
      : ""
  }`}
>
  <div className="flex items-center justify-between mb-2">
    <FieldLabel
      label={lang === "AR" ? "الوسائط" : "Media"}
      hint={
        lang === "AR"
          ? "أضف صورة واحدة على الأقل للمنتج."
          : "Add at least one image."
      }
      valid={images.length > 0}
      error={
            showValidation && images.length === 0
              ? (lang === "AR" ? "مطلوب صورة واحدة على الأقل" : "At least 1 image required")
              : ""
          }
    />

    {images.length >= 3 && (
      <button
        type="button"
        onClick={() =>
          document.getElementById("multi-image-upload").click()
        }
        className="
          flex items-center gap-1
          text-xs font-medium
          px-3 py-1.5
          border border-[#c9cccf]
          rounded-[8px]
          bg-white
          hover:bg-[#f6f6f7]
        "
      >
        <FaPlus className="text-[10px]" />
        {lang === "AR" ? "إضافة" : "Add"}
      </button>
    )}
  </div>

  {/* INPUT */}
  <input
    id="multi-image-upload"
    type="file"
    accept="image/*,video/*"
    multiple
    className="hidden"
    onChange={(e) => {
      const files = Array.from(e.target.files || []);
      handleMultiImageUpload(files);
      e.target.value = "";
    }}
  />

  {/* EMPTY STATE */}
  {images.length === 0 && (
    <div
      onClick={() =>
        document.getElementById("multi-image-upload").click()
      }
      className="
  border-2 border-dashed border-gray-400
  rounded-[12px]
  bg-white
  hover:bg-[#f1f2f3]
  hover:border-[#2c6ecb]
  transition
  cursor-pointer
  flex flex-col items-center justify-center
  text-center
  px-6 py-7
"
    >
      <FaImage className="text-[28px] text-gray-400 mb-3" />

      <span className="px-3 py-1.5 bg-white border border-gray-500 rounded-[8px] text-sm font-medium">
        {lang === "AR" ? "رفع جديد" : "Upload new"}
      </span>

      <p className="text-xs text-gray-500 mt-2">
        {lang === "AR"
          ? "يمكنك رفع صور أو فيديو"
          : "Accepts images and videos"}
      </p>
    </div>
  )}

  {/* IMAGE LIST */}
  {images.length > 0 && (
    <div className="mt-4 overflow-x-auto">
      <div className="flex gap-3 pb-2 min-w-max">

        {images.map((img, idx) => (
          <div
            key={idx}
            className="w-[170px] shrink-0 border border-[#e1e3e5] rounded-[12px] bg-white overflow-hidden"
          >
            {/* PREVIEW */}
            <div className="h-[100px] bg-[#f6f6f7] flex items-center justify-center">
              {img.url ? (
                img.media_type === "video" ? (
                  <video src={img.url} className="h-full object-contain" />
                ) : (
                  <img src={img.url} className="h-full object-contain" />
                )
              ) : (
                <FaImage className="text-gray-300 text-xl" />
              )}
            </div>

            {/* CONTENT */}
            <div className="p-2 space-y-2">

              {/* SORT */}
              <div>
                <div className="text-[10px] text-gray-500 mb-1">
                  {TEXT.sortOrder}
                </div>
                <input
                  type="number"
                  value={img.sort_order ?? ""}
                  onChange={(e) =>
                    updateImageRow(idx, {
                      sort_order: Number(e.target.value || 0),
                    })
                  }
                  className="w-full h-[28px] text-xs border rounded px-2"
                />
              </div>

              {/* COLOR */}
              <div>
                <div className="text-[10px] text-gray-500 mb-1">
                  {lang === "AR" ? "اللون" : "Color"}
                </div>
                <input
                  value={img.color_name || ""}
                  onChange={(e) =>
                    updateImageRow(idx, {
                      color_name: e.target.value,
                    })
                  }
                  className="w-full h-[28px] text-xs border rounded px-2"
                />
              </div>

              {/* REMOVE */}
              <button
                type="button"
                onClick={() => removeImageRow(idx)}
                className="w-full text-[11px] text-red-600 hover:underline pt-1"
              >
                {TEXT.remove}
              </button>
            </div>
          </div>
        ))}

        {/* ADD CARD */}
        <div
          onClick={() =>
            document.getElementById("multi-image-upload").click()
          }
          className="
            w-[120px] h-[110px]
            shrink-0
            border-2 border-dashed border-gray-400
            rounded-[12px]
            bg-gray-100
            flex items-center justify-center
            cursor-pointer
            hover:bg-[#f6f6f7]
            mt-12
          "
        >
          <FaPlus className="text-gray-400 text-xl" />
        </div>

      </div>
    </div>
  )}
</section>
</div>
</section>

 {/* VARIANTS CARD */}
<section className="mt-3 bg-white border border-[#e3e3e3] rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
  <div className="p-4 space-y-4">

    <div className="flex items-center justify-between">
      <div className="mb-1">
        <FieldLabel
          label={lang === "AR" ? "المتغيرات" : "Variants"}
          hint={
            lang === "AR"
              ? "أضف ألوان ومقاسات للمنتج مع الكمية لكل خيار"
              : "Add colors and sizes for this product with stock quantities"
          }
          valid={variants.length > 0}
        />
      </div>

      {groupedVariants.length > 0 && (
        <button
          type="button"
          onClick={() => setShowColorModal(true)}
          className="px-3 py-1.5 text-xs rounded-[8px] bg-[#202223] text-white flex items-center gap-1"
        >
          <FaPlus className="text-[10px]" />
          {lang === "AR" ? "إضافة لون" : "Add Color"}
        </button>
      )}
    </div>

    {groupedVariants.length === 0 ? (
      <button
        type="button"
        onClick={() => setShowColorModal(true)}
        className="w-full border border-dashed border-gray-400 rounded-[12px] bg-white hover:bg-[#f6f6f7] transition py-4 flex flex-col items-center justify-center gap-2"
      >
        <div className="w-6 h-6 rounded-full border border-gray-400 flex items-center justify-center">
          <FaPlus className="text-gray-500 text-sm" />
        </div>

        <span className="text-[12px] text-gray-600 font-medium">
          {lang === "AR" ? "إضافة لون ومقاس" : "Add size and color"}
        </span>
      </button>
    ) : (
      groupedVariants.map((g, idx) => (
        <div key={idx} className="border rounded-[10px] p-3 space-y-3">

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => removeColorGroup(idx)}
              className="text-gray-400 hover:text-red-600"
            >
              <FaTrash className="text-sm" />
            </button>
          </div>

          {/* COLOR */}
          <div
            onClick={() => {
              setSelectedColor({ name: g.color_name, hex: g.color_hex });
              setShowColorModal(true);
              setSelectedVariantIndex(idx);
            }}
            className="flex items-center gap-2 cursor-pointer"
          >
            <div
              className="w-6 h-6 rounded-full border"
              style={{ backgroundColor: g.color_hex }}
            />
            <span className="text-sm font-medium">{g.color_name}</span>
            <span className="text-xs text-gray-400">{g.color_hex}</span>
          </div>

          {/* SIZES */}
          {g.rows.map((r, rowIdx) => (
            <div key={rowIdx} className="flex gap-2">

              <input
                value={r.size}
                onChange={(e) =>
                  updateSizeRow(idx, rowIdx, { size: e.target.value })
                }
                placeholder={lang === "AR" ? "المقاس" : "Size"}
                className="h-[30px] text-xs border rounded px-2"
              />

              <input
                type="number"
                value={r.stock_qty}
                onChange={(e) =>
                  updateSizeRow(idx, rowIdx, {
                    stock_qty: Number(e.target.value || 0),
                  })
                }
                placeholder={lang === "AR" ? "الكمية" : "Qty"}
                className="h-[30px] text-xs border rounded px-2"
              />

              {rowIdx !== 0 && (
                <button
                  type="button"
                  onClick={() => removeSizeRow(idx, rowIdx)}
                  className="text-red-600 text-xs"
                >
                  ✕
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={() => addSizeRow(idx)}
            className="text-xs text-blue-600"
          >
            + {lang === "AR" ? "إضافة مقاس" : "Add Size"}
          </button>
        </div>
      ))
    )}
  </div>
</section>
    
   {/* CARD 3 — PRICING (SHOPIFY STYLE SAME AS ADD PRODUCT) */}
<section className="mt-3 bg-white border border-[#e3e3e3] rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]">

  <div className="px-5 py-4">
    
    {/* HEADER */}
    <div className="mb-3">
      <FieldLabel
        label={TEXT.pricing}
        hint={
          lang === "AR"
            ? "حدد سعر المنتج ويمكنك إضافة تخفيض"
            : "Set the product price and optionally add a discount"
        }
      />
    </div>

    {/* CONTENT */}
    <div className="space-y-4">

      {/* BASE PRICE */}
      <div>
  <label className="text-xs text-gray-500 mb-1 block">
    {TEXT.price}
  </label>

  <input
    ref={refPrice}
    type="number"
    value={price}
    onChange={(e) => setPrice(e.target.value)}
    className={inputClass(
      showValidation && (price === "" || Number(price) <= 0)
    )}
    placeholder="0.00"
  />

  {showValidation && (price === "" || Number(price) <= 0) && (
    <p className="text-[11px] text-red-500 mt-1">
      {lang === "AR"
        ? "السعر يجب أن يكون أكبر من 0"
        : "Price must be greater than 0"}
    </p>
  )}
</div>

      {/* ON SALE TOGGLE */}
      <div className="flex items-center justify-between pt-2 border-t">
        <span className="text-sm font-medium">
          {TEXT.sale}
        </span>

        <div
          onClick={() => {
            setOnSale(!onSale);
            if (!onSale) setDiscountInput("");
          }}
          className={`w-10 h-6 flex items-center rounded-full p-[2px] cursor-pointer transition
            ${onSale ? "bg-[#202223]" : "bg-[#8c9196]"}`}
        >
          <div
            className={`w-5 h-5 bg-white rounded-full shadow-md transform transition
              ${toggleTranslate(onSale)}`}
          />
        </div>
      </div>

      {/* SALE BOX */}
      {onSale && (
        <div className="bg-[#f6f6f7] border border-[#e1e3e5] rounded-[10px] p-3 space-y-3">

          <div className="grid grid-cols-2 gap-3">

            {/* SALE % */}
            <div>
              <label className="text-[11px] text-gray-500 mb-1 block">
                {TEXT.salePercent}
              </label>

              <input
                type="number"
                value={salePercent}
                onChange={(e) => {
                  setSalePercent(e.target.value);
                  setDiscountInput("");
                }}
                className={inputClass(false)}
              />
            </div>

            {/* DISCOUNTED PRICE */}
            <div>
              <label className="text-[11px] text-gray-500 mb-1 block">
                {TEXT.discountedPrice}
              </label>

              <input
                type="number"
                value={discountInput || discountedPrice}
                onChange={(e) => {
                  const val = e.target.value;
                  setDiscountInput(val);

                  const base = Number(price);
                  const discounted = Number(val);

                  if (base > 0 && discounted >= 0 && discounted <= base) {
                    const percent = ((base - discounted) / base) * 100;
                    setSalePercent(percent.toFixed(2));
                  }
                }}
                className={inputClass(false)}
              />
            </div>

          </div>

          {/* PREVIEW */}
          {discountedPrice && (
            <div className="text-xs text-gray-600 pt-1">
              <span className="line-through mr-2">
                ${Number(price || 0).toFixed(2)}
              </span>

              <span className="font-semibold text-black">
                ${discountedPrice}
              </span>
            </div>
          )}

        </div>
      )}

    </div>
  </div>
</section>
</div>

{/* RIGHT COLUMN */}
<div className="space-y-3 contents xl:block">

  {/* PRODUCT IDENTIFIER */}
  <section className="bg-white border border-[#e3e3e3] rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
    <div className="p-4 space-y-3">

      <div className="space-y-2">
        <div>
          <div className="mb-1">
            <FieldLabel
              label={TEXT.publicId}
              hint={
                lang === "AR"
                  ? "معرف فريد للاستخدام الداخلي (مطلوب)"
                  : "Unique identifier for internal use (required)"
              }
              valid={publicId.trim().length > 0}
              error={showValidation && !publicId.trim() ? "Required" : ""}
            />
          </div>

          <input
            ref={refPublicId}
            value={publicId}
            onChange={(e) => setPublicId(e.target.value)}
            className={inputClass(showValidation && !publicId.trim())}
          />
        </div>
      </div>

    </div>
  </section>

 {/* CATEGORIES */}
<section
  ref={refCategories}
  className={`bg-white border rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]
  ${
    showValidation && !selectedCategories.length
      ? "border-red-500"
      : "border-[#e3e3e3]"
  }`}
>
  <div className="p-4 space-y-3">

    <div className="mb-1">
      <FieldLabel
        label={TEXT.categories}
        hint={
          lang === "AR"
            ? "اختر التصنيف الذي ينتمي إليه المنتج (مطلوب)"
            : "Select product collections (required)"
        }
        valid={selectedCategories.length > 0}
        error={
                  showValidation && !selectedCategories.length
                    ? TEXT.selectAtLeastOneCategory
                    : ""
                }
      />
    </div>

    {/* SEARCH */}
    <div className="relative category-dropdown">
      <div className="flex items-center gap-2 border border-gray-500 rounded-[10px] px-3 h-[36px] bg-white">
        <FaSearch className="text-gray-400 text-[13px]" />

        <input
          value={categorySearch}
          onChange={(e) => {
            setCategorySearch(e.target.value);
            setIsCategoryOpen(true);
          }}
          onFocus={() => setIsCategoryOpen(true)}
          placeholder={lang === "AR" ? "ابحث..." : "Search..."}
          className="flex-1 text-sm outline-none bg-transparent"
        />
      </div>

      {/* DROPDOWN */}
      {isCategoryOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#c9cccf] rounded-xl shadow-md max-h-56 overflow-y-auto">

          {filteredCategories.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500 text-center">
              {lang === "AR" ? "لا توجد نتائج" : "No results"}
            </div>
          ) : (
            filteredCategories.map((c) => {
              const cid = Number(c.id);
              const checked = selectedCategories.includes(cid);

              return (
                <div
  key={c.id}
  onClick={() => {
    const isAlready = selectedCategories.includes(cid);

    setSelectedCategories((prev) =>
      toggleIdInArray(prev, cid)
    );

    if (!isAlready) setIsCategoryOpen(false); // close only on select
  }}
  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
>
  <div
    className={`
      w-5 h-5 rounded-[6px] border flex items-center justify-center
      ${checked
        ? "bg-[#202223] border-[#202223]"
        : "border-[#c9cccf]"}
    `}
  >
    {checked && <FaCheck className="text-white text-[10px]" />}
  </div>

  <span>
  {truncate(
    lang === "AR"
      ? c.name_ar || c.name_en
      : c.name_en || c.name_ar
  )}
</span>
</div>
              );
            })
          )}
        </div>
      )}
    </div>

    {/* SELECTED */}
    {selectedCategories.length > 0 && (
      <div className="flex flex-wrap gap-2">
        {selectedCategories.map((cid) => {
          const found = allCategories.find((c) => Number(c.id) === cid);

          return (
            <div
              key={cid}
              className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#f1f2f3] text-xs"
            >
              <span>
  {truncate(
    lang === "AR"
      ? found?.name_ar || found?.name_en
      : found?.name_en || found?.name_ar
  )}
</span>

              <button
                type="button"
                onClick={() =>
                  setSelectedCategories((prev) =>
                    prev.filter((id) => id !== cid)
                  )
                }
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
    )}
  </div>
</section>

  {/* TAGS */}
<section className="bg-white border border-[#e3e3e3] rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
  <div className="p-4 space-y-3">

    <div className="mb-1">
      <FieldLabel
        label={TEXT.tags}
        hint={
          lang === "AR"
            ? "اختياري - لتحسين البحث"
            : "Optional - improves search"
        }
        valid={selectedTags.length > 0}
      />
    </div>

    {/* SEARCH */}
    <div className="relative tag-dropdown">
      <div className="flex items-center gap-2 border border-gray-500 rounded-[10px] px-3 h-[36px] bg-white">
        <FaSearch className="text-gray-400 text-[13px]" />

        <input
          value={tagSearch}
          onChange={(e) => {
            setTagSearch(e.target.value);
            setIsTagOpen(true);
          }}
          onFocus={() => setIsTagOpen(true)}
          placeholder={lang === "AR" ? "ابحث..." : "Search tags..."}
          className="flex-1 text-sm outline-none bg-transparent"
        />
      </div>

      {/* DROPDOWN */}
      {isTagOpen && (
  <div className="absolute z-50 mt-1 w-full bg-white border border-[#c9cccf] rounded-xl shadow-md max-h-56 overflow-y-auto">

    {/* 🔥 ADD TAG BUTTON (FIRST ROW) */}
    <button
      type="button"
      onClick={() => {
        setShowTagModal(true);
        setIsTagOpen(false);
      }}
      className="
        w-full flex items-center gap-2 px-3 py-2
        bg-[#f6f6f7] hover:bg-[#eef0f1]
        text-[#202223] text-sm
        border-b border-[#e1e3e5]
      "
    >
      <FaPlus className="text-[12px]" />
      <span>
        {lang === "AR" ? "إضافة وسم جديد" : "Add new tag"}
      </span>
    </button>

    {/* EXISTING TAGS */}
    {filteredTags.map((t) => {
            const tid = Number(t.id);
            const checked = selectedTags.includes(tid);

            return (
              <div
  key={t.id}
  onClick={() => {
    const isAlready = selectedTags.includes(tid);

    setSelectedTags((prev) =>
      toggleIdInArray(prev, tid)
    );

    if (!isAlready) setIsTagOpen(false); // close only on select
  }}
  className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer
    ${checked ? "bg-[#f1f2f3]" : "hover:bg-gray-100"}`}
>
  <div
    className={`
      w-5 h-5 rounded-[6px] border flex items-center justify-center
      ${checked
        ? "bg-[#202223] border-[#202223]"
        : "border-[#c9cccf]"}
    `}
  >
    {checked && <FaCheck className="text-white text-[10px]" />}
  </div>

  <span>
    {truncate(
      lang === "AR"
        ? t.label_ar || t.label_en || t.slug
        : t.label_en || t.slug
    )}
  </span>
</div>
            );
          })}
        </div>
      )}
    </div>

    {/* SELECTED */}
    {selectedTags.length > 0 && (
      <div className="flex flex-wrap gap-2">
        {selectedTags.map((tid) => {
          const found = allTags.find((t) => Number(t.id) === tid);

          return (
            <div
              key={tid}
              className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#f1f2f3] text-xs"
            >
              <span>
  {truncate(
    lang === "AR"
      ? found?.label_ar || found?.label_en || found?.slug
      : found?.label_en || found?.slug
  )}
</span>

              <button
                type="button"
                onClick={() =>
                  setSelectedTags((prev) =>
                    prev.filter((id) => id !== tid)
                  )
                }
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
    )}

  </div>
</section>

  {/* STATUS */}
<section className="bg-white border border-[#e3e3e3] rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
  <div className="p-4 space-y-3">

    <div className="mb-2">
      <FieldLabel
        label={lang === "AR" ? "الحالة" : "Status"}
        hint={
  lang === "AR"
    ? "حدد إذا كان المنتج جديد أو الأكثر مبيعًا أو يباع بسرعة"
    : "Mark this product as new, bestseller, or quickly sold out"
}
valid={isBestseller || isNew || isQuicklySoldOut}
      />
    </div>

    {/* BESTSELLER */}
    <div className="flex items-center justify-between text-sm">
      <span>{TEXT.bestseller}</span>

      <div
        onClick={() => setIsBestseller(!isBestseller)}
        className={`w-10 h-6 flex items-center rounded-full p-[2px] cursor-pointer transition
          ${isBestseller ? "bg-[#202223]" : "bg-[#8c9196]"}`}
      >
        <div
          className={`w-5 h-5 bg-white rounded-full shadow-md transform transition
            ${toggleTranslate(isBestseller)}`}
        />
      </div>
    </div>

    {/* NEW */}
    <div className="flex items-center justify-between text-sm">
      <span>{TEXT.new}</span>

      <div
        onClick={() => setIsNew(!isNew)}
        className={`w-10 h-6 flex items-center rounded-full p-[2px] cursor-pointer transition
          ${isNew ? "bg-[#202223]" : "bg-[#8c9196]"}`}
      >
        <div
          className={`w-5 h-5 bg-white rounded-full shadow-md transform transition
            ${toggleTranslate(isNew)}`}
        />
      </div>
    </div>

{/* QUICKLY SOLD OUT */}
<div className="flex items-center justify-between text-sm">
  <span>{TEXT.quicklySoldOut}</span>

  <div
    onClick={() => setIsQuicklySoldOut(!isQuicklySoldOut)}
    className={`w-10 h-6 flex items-center rounded-full p-[2px] cursor-pointer transition
      ${isQuicklySoldOut ? "bg-[#202223]" : "bg-[#8c9196]"}`}
  >
    <div
      className={`w-5 h-5 bg-white rounded-full shadow-md transform transition
        ${toggleTranslate(isQuicklySoldOut)}`}
    />
  </div>
</div>

  </div>
</section>

</div>
            </div>
          </form>
          {/* ACTION BUTTONS */}
            <div className="flex justify-end mt-6">
              <button
  type="button"
  onClick={handleSave}
  disabled={!isDirty || saving}
  className={`
    flex items-center gap-2
    px-3 h-[30px]
    rounded-[10px]
    text-sm font-medium
    text-white
    bg-[#202223]

    shadow-[0_2px_0_0_#000]
    hover:bg-black
    hover:translate-y-[1px]
    hover:shadow-[0_1px_0_0_#000]

    active:translate-y-[2px]
    active:shadow-none

    transition-all duration-150

    disabled:opacity-50 disabled:cursor-not-allowed
  `}
>
  {saving ? TEXT.saving : TEXT.save}
</button>
            </div>
          </div>
        </main>
      </div>
    </div>
   </>
  );
}

export default AdminEditProduct;

